/**
 * Dónde se está mirando en el inicio (espacio de trabajo y carpeta) y, por lo tanto, dónde se
 * crean los proyectos nuevos (plantillas, asistente, importación). Se recuerda en este navegador.
 */

import type { Proyecto } from '../../nucleo/dominio/espacio';
import { ESPACIO_DE_TRABAJO_PROPIO } from '../../nucleo/dominio/organizacion';
import { repositorio } from './repositorio';

/** `todas` y `sin-carpeta` son vistas; cualquier otro valor es el id de una carpeta. */
export type FiltroCarpeta = 'todas' | 'sin-carpeta' | string;

export interface Ubicacion {
  espacioDeTrabajoId: string;
  carpeta: FiltroCarpeta;
}

const CLAVE = 'gemba.inicio.ubicacion';
const PREDETERMINADA: Ubicacion = {
  espacioDeTrabajoId: ESPACIO_DE_TRABAJO_PROPIO,
  carpeta: 'todas',
};

export function leerUbicacion(): Ubicacion {
  try {
    const u = JSON.parse(localStorage.getItem(CLAVE) ?? 'null') as Partial<Ubicacion> | null;
    if (u && typeof u.espacioDeTrabajoId === 'string' && typeof u.carpeta === 'string')
      return { espacioDeTrabajoId: u.espacioDeTrabajoId, carpeta: u.carpeta };
  } catch {
    // Sin almacenamiento disponible: se usa la ubicación predeterminada.
  }
  return PREDETERMINADA;
}

export function guardarUbicacion(u: Ubicacion): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(u));
  } catch {
    // Es solo una comodidad: si no se puede recordar, el inicio vuelve a «Mis proyectos».
  }
}

export const carpetaDeDestino = (u: Ubicacion): string | null =>
  u.carpeta === 'todas' || u.carpeta === 'sin-carpeta' ? null : u.carpeta;

/**
 * Lleva un proyecto nuevo a la ubicación elegida en el inicio, si sigue existiendo; si no, a «Mis
 * proyectos». Devuelve también si ese espacio de trabajo tiene datos reales.
 */
export async function ubicarProyectoNuevo(
  p: Proyecto,
): Promise<{ proyecto: Proyecto; datosReales: boolean }> {
  const u = leerUbicacion();
  const espacio =
    (await repositorio().obtenerEspacioDeTrabajo(u.espacioDeTrabajoId)) ??
    (await repositorio().obtenerEspacioDeTrabajo(ESPACIO_DE_TRABAJO_PROPIO))!;
  const carpetaId = carpetaDeDestino(u);
  const carpetas =
    espacio.id === u.espacioDeTrabajoId && carpetaId
      ? await repositorio().listarCarpetas(espacio.id)
      : [];
  const { carpetaId: _c, ...resto } = p;
  const proyecto: Proyecto = { ...resto, espacioDeTrabajoId: espacio.id };
  if (carpetaId && carpetas.some((c) => c.id === carpetaId)) proyecto.carpetaId = carpetaId;
  return { proyecto, datosReales: espacio.contieneDatosReales };
}
